/**
 * Reviews Carousel – Scoped JS
 * Custom element: <reviews-carousel>
 * Handles: arrow nav, dot sync, autoplay, keyboard nav
 */
if (!customElements.get('reviews-carousel')) {
  class ReviewsCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.cards = [];
      this.prevBtn = null;
      this.nextBtn = null;
      this.dotsContainer = null;
      this.dots = [];
      this.autoplayInterval = null;
      this.currentIndex = 0;
      this._resizeObserver = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-rc-track]');
      if (!this.track) return;

      this.cards = [...this.track.children];
      if (this.cards.length === 0) return;

      this.prevBtn = this.querySelector('[data-rc-prev]');
      this.nextBtn = this.querySelector('[data-rc-next]');
      this.dotsContainer = this.querySelector('[data-rc-dots]');

      this._bindArrows();
      this._buildDots();
      this._syncState();

      // Scroll listener (debounced)
      let scrollTimer;
      this.track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this._syncState(), 80);
      }, { passive: true });

      // Keyboard nav on track
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this._scrollBy(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this._scrollBy(-1); }
      });

      // Autoplay
      const autoplaySpeed = parseInt(this.dataset.autoplay, 10);
      if (autoplaySpeed > 0) {
        this._startAutoplay(autoplaySpeed * 1000);
        this.addEventListener('mouseenter', () => this._stopAutoplay());
        this.addEventListener('mouseleave', () => this._startAutoplay(autoplaySpeed * 1000));
        this.addEventListener('focusin', () => this._stopAutoplay());
        this.addEventListener('focusout', () => this._startAutoplay(autoplaySpeed * 1000));
      }

      // Resize → re-sync
      this._resizeObserver = new ResizeObserver(() => this._syncState());
      this._resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._resizeObserver) this._resizeObserver.disconnect();
    }

    _bindArrows() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._scrollBy(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._scrollBy(1));
    }

    _buildDots() {
      if (!this.dotsContainer) return;
      // Calculate how many "pages"
      this._updateDotCount();
    }

    _updateDotCount() {
      if (!this.dotsContainer) return;
      const pages = this._getPageCount();
      const currentDots = this.dotsContainer.children.length;
      if (currentDots === pages) return;

      this.dotsContainer.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.className = 'rc-dot';
        dot.setAttribute('aria-label', `Go to slide group ${i + 1}`);
        dot.addEventListener('click', () => this._goToPage(i));
        this.dotsContainer.appendChild(dot);
      }
      this.dots = [...this.dotsContainer.children];
    }

    _getPageCount() {
      if (!this.track || this.cards.length === 0) return 1;
      const trackWidth = this.track.offsetWidth;
      const scrollWidth = this.track.scrollWidth;
      if (trackWidth >= scrollWidth) return 1;
      const cardWidth = this.cards[0].offsetWidth;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const cardsPerView = Math.max(1, Math.floor((trackWidth + gap) / (cardWidth + gap)));
      return Math.ceil(this.cards.length / cardsPerView);
    }

    _getCardsPerView() {
      if (!this.track || this.cards.length === 0) return 1;
      const trackWidth = this.track.offsetWidth;
      const cardWidth = this.cards[0].offsetWidth;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      return Math.max(1, Math.floor((trackWidth + gap) / (cardWidth + gap)));
    }

    _scrollBy(direction) {
      if (!this.track || this.cards.length === 0) return;
      const cardWidth = this.cards[0].offsetWidth;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const scrollAmount = (cardWidth + gap) * direction;
      this.track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }

    _goToPage(pageIndex) {
      if (!this.track || this.cards.length === 0) return;
      const cardsPerView = this._getCardsPerView();
      const targetCard = this.cards[pageIndex * cardsPerView];
      if (targetCard) {
        const trackRect = this.track.getBoundingClientRect();
        const cardRect = targetCard.getBoundingClientRect();
        const scrollLeft = this.track.scrollLeft + (cardRect.left - trackRect.left);
        this.track.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }

    _syncState() {
      if (!this.track || this.cards.length === 0) return;

      const scrollLeft = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.offsetWidth;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft >= maxScroll - 2;

      // Arrows
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      // Current page
      this._updateDotCount();
      if (this.dots.length > 0) {
        const cardsPerView = this._getCardsPerView();
        const cardWidth = this.cards[0].offsetWidth;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        const currentPage = Math.round(scrollLeft / ((cardWidth + gap) * cardsPerView));
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('rc-dot--active', i === currentPage);
        });
      }
    }

    _startAutoplay(ms) {
      this._stopAutoplay();
      this.autoplayInterval = setInterval(() => {
        const maxScroll = this.track.scrollWidth - this.track.offsetWidth;
        if (this.track.scrollLeft >= maxScroll - 2) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this._scrollBy(1);
        }
      }, ms);
    }

    _stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }
  }

  customElements.define('reviews-carousel', ReviewsCarousel);
}
